import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';

export interface UpsellReward {
  /** Clé de rendu — id de la promotion ou du groupe de questions. */
  id: string;
  /** Ce que le client débloque : « -15% sur toute la commande », « Un coca offert ». */
  reward: string;
  /** Ce qu'il manque pour l'atteindre, en centimes. */
  gapCents: number;
}

interface Props {
  open: boolean;
  rewards: UpsellReward[];
  /** Referme le panier et renvoie le client au menu. */
  onAddMore: () => void;
  /** Poursuit vers le paiement sans rien ajouter. */
  onContinue: () => void;
  /** Échap / croix / fond : on reste dans le panier. */
  onClose: () => void;
}

/**
 * La relance « il vous manque X € » au moment où le client clique sur
 * « Passer la commande ».
 *
 * C'est le dernier instant où ajouter un plat est encore à un clic : le menu
 * est derrière le tiroir, alors qu'à la page de paiement le client a déjà
 * commencé à saisir ses coordonnées et repartir lui coûte son formulaire. Le
 * tiroir affiche déjà le rappel en rouge, mais un bandeau se lit passivement —
 * ici il faut répondre.
 *
 * Le CartDrawer ne l'ouvre qu'une fois par montant de panier : une relance qui
 * revient à chaque clic est un obstacle, pas une envie.
 */
export default function UpsellModal({ open, rewards, onAddMore, onContinue, onClose }: Props) {
  const { t } = useTranslation();
  const euros = (cents: number) => (cents / 100).toFixed(2).replace('.', ',');

  return (
    <Modal open={open} onClose={onClose} ariaLabel={t('cart.upsell.title')}>
      <div
        className="px-4 py-3 border-b-2 border-ink flex items-center justify-between"
        data-testid="cart-upsell-modal"
      >
        <h2 className="font-display text-xl m-0">🎁 {t('cart.upsell.title')}</h2>
        <button
          type="button"
          onClick={onClose}
          className="bg-hot-pink text-[var(--on-accent)] border-2 border-ink rounded-btn font-display text-xl leading-none w-8 h-8 flex items-center justify-center"
          aria-label={t('cart.close')}
        >×</button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <p className="font-mono text-sm text-ink/80 mt-0">{t('cart.upsell.body')}</p>
        <ul className="list-none pl-0 m-0 space-y-2">
          {rewards.map((entry) => (
            <li
              key={entry.id}
              className="border-2 border-ink rounded-btn px-3 py-2 text-white shadow-stamp-sm"
              style={{ background: 'var(--sale-red)' }}
            >
              <span className="font-display text-base block">{entry.reward}</span>
              <span className="font-mono text-xs block">
                {t('cart.upsell.missing', { amount: euros(entry.gapCents) })}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="border-t-2 border-ink p-4 bg-cream flex flex-col gap-2">
        <button
          type="button"
          onClick={onAddMore}
          className="w-full bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn py-3 font-display shadow-stamp-sm"
          data-testid="cart-upsell-add-more"
        >
          {t('cart.upsell.addMore')}
        </button>
        <button
          type="button"
          onClick={onContinue}
          className="w-full bg-paper border-2 border-ink rounded-btn py-2 font-display text-sm"
          data-testid="cart-upsell-continue"
        >
          {t('cart.upsell.orderAnyway')}
        </button>
      </div>
    </Modal>
  );
}
